import React, { Suspense, useEffect } from 'react';
import { createBrowserRouter, RouterProvider, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { redirects } from './routes';
import LoadingSpinner from '../components/LoadingSpinner';
import ScrollToTop from '../components/ScrollToTop';
import LazyErrorBoundary from '../utils/LazyErrorBoundary';
import URLErrorDetector from '../components/URLErrorDetector';

// Direkte Imports statt Lazy Loading für besseres Debugging
import HomePage from '../pages/HomePage';
import AGBPage from '../pages/AGBPage';
import ImpressumPage from '../pages/ImpressumPage';
import PrivacyPage from '../pages/PrivacyPage';
import AffiliatePage from '../pages/AffiliatePage';
import BeratungsterminPage from '../pages/BeratungsterminPage';
import NotFoundPage from '../pages/NotFoundPage';

// Root Layout Komponente
const RootLayout: React.FC = () => {
  return (
    <HelmetProvider>
      <ScrollToTop />
      <LazyErrorBoundary>
        <URLErrorDetector>
          <Suspense fallback={<LoadingSpinner size="large" text="Seite wird geladen..." />}>
            <RedirectHandler />
            <Outlet />
          </Suspense>
        </URLErrorDetector>
      </LazyErrorBoundary>
    </HelmetProvider>
  );
};

// Redirect Handler für Legacy URLs
const RedirectHandler: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const currentPath = location.pathname;
    const redirectTarget = redirects[currentPath];
    
    // Debug-Log hinzufügen
    console.log('Current path:', currentPath);
    console.log('Redirect target:', redirectTarget);
    
    if (redirectTarget) {
      console.log('Redirecting to:', redirectTarget);
      // 301 Redirect für SEO
      navigate(redirectTarget, { replace: true });
    }
  }, [location.pathname, navigate]);

  return null;
};

// Router-Konfiguration
const router = createBrowserRouter([
  {
    path: '/',
    element: <RootLayout />,
    errorElement: <NotFoundPage />,
    children: [
      // Direkte Route-Definitionen
      {
        index: true,
        element: <HomePage />
      },
      {
        path: 'agb',
        element: <AGBPage />
      },
      {
        path: 'impressum',
        element: <ImpressumPage />
      },
      {
        path: 'datenschutz',
        element: <PrivacyPage />
      },
      {
        path: 'affiliate',
        element: <AffiliatePage />
      },
      {
        path: 'beratungstermin',
        element: <BeratungsterminPage />
      },
      // 404 Fallback
      {
        path: '*',
        element: <NotFoundPage />
      }
    ]
  }
]);

// Router Provider Komponente
export const AppRouter: React.FC = () => {
  return <RouterProvider router={router} />;
};
